import { locationNameHeading, storeIdHeading, cloneHeading, cloneFromHeading, createPhoneNumberHeading, locationWebsiteUrlHeading } from "../../../../../shared/constants/sparkConstants/formFieldImportHeadings";

export const locationImportHeadings = [locationNameHeading, storeIdHeading, cloneHeading, cloneFromHeading, createPhoneNumberHeading, locationWebsiteUrlHeading];

export const processLocationCSV = (csvArray: string[][]): { [key: string]: string }[] => {
    const headers = (csvArray[0] ?? []).map(header => header.replace(/^\uFEFF/, '').trim().toLowerCase());
    // Keep support for older two-column files whose headings were ignored.
    const legacyHeadings = !headers.some(heading => locationImportHeadings.slice(2).includes(heading));
    const nameIndex = headers.indexOf(locationNameHeading) >= 0 ? headers.indexOf(locationNameHeading) : 0;
    return csvArray.slice(1).filter(row => !!row[nameIndex]?.trim()).map(row => {
        const result: { [key: string]: string } = {};
        locationImportHeadings.forEach((heading, index) => {
            const column = headers.indexOf(heading);
            const fallback = legacyHeadings && index < 2 ? index : -1;
            result[heading] = row[column >= 0 ? column : fallback]?.trim() ?? '';
        });
        return result;
    });
};

interface LocationImportOptions {
    locationCloneEnabled?: Record<number, boolean>;
    locationAutogenerateEnabled?: Record<number, boolean>;
    locationCloneSourceAppIds?: Record<number, string>;
    locationCreatePhoneNumber?: Record<number, boolean>;
    locationWebsiteUrls?: Record<number, string>;
}

export const mergeImportedLocations = (
    locationNames: string[],
    storeIds: Record<number, string>,
    importedData: { [key: string]: string }[],
    append: boolean,
    options: LocationImportOptions = {},
    sourceApps: { id?: string; name?: string }[] = []
) => {
    const nextLocationNames = append ? [...locationNames] : [];
    const nextStoreIds: Record<number, string> = append ? { ...storeIds } : {};
    const locationCloneEnabled = append ? { ...options.locationCloneEnabled } : {};
    const locationAutogenerateEnabled = append ? { ...options.locationAutogenerateEnabled } : {};
    const locationCloneSourceAppIds = append ? { ...options.locationCloneSourceAppIds } : {};
    const locationCreatePhoneNumber = append ? { ...options.locationCreatePhoneNumber } : {};
    const locationWebsiteUrls = append ? { ...options.locationWebsiteUrls } : {};
    const errors: string[] = [];

    const parseBoolean = (value: string, heading: string, rowNumber: number) => {
        const normalized = value.trim().toLowerCase();
        if (['true', 'yes', '1'].includes(normalized)) return true;
        if (['', 'false', 'no', '0'].includes(normalized)) return false;
        errors.push(`Row ${rowNumber}: ${heading} must be TRUE/FALSE, YES/NO, or 1/0.`);
        return false;
    };

    importedData.forEach((row, rowIndex) => {
        const index = nextLocationNames.length;
        nextLocationNames.push(row[locationNameHeading] ?? '');
        nextStoreIds[index] = row[storeIdHeading] ?? '';
        const source = (row[cloneFromHeading] ?? '').trim();
        const cloneValue = row[cloneHeading] ?? '';
        const clone = cloneValue.trim() ? parseBoolean(cloneValue, cloneHeading, rowIndex + 2) : !!source;
        locationCloneEnabled[index] = clone;
        locationAutogenerateEnabled[index] = clone;
        locationCreatePhoneNumber[index] = parseBoolean(row[createPhoneNumberHeading] ?? '', createPhoneNumberHeading, rowIndex + 2);
        locationWebsiteUrls[index] = row[locationWebsiteUrlHeading] ?? '';
        if (clone) {
            const byId = sourceApps.filter(app => !!app.id && app.id === source);
            const matches = byId.length ? byId : sourceApps.filter(app => !!app.id && !!source && app.name?.trim().toLowerCase() === source.toLowerCase());
            if (matches.length === 1) {
                locationCloneSourceAppIds[index] = matches[0].id;
            } else {
                errors.push(`Row ${rowIndex + 2}: clone from must match one existing location name or application ID.`);
            }
        }
    });

    return { locationNames: nextLocationNames, storeIds: nextStoreIds, locationCloneEnabled, locationAutogenerateEnabled, locationCloneSourceAppIds, locationCreatePhoneNumber, locationWebsiteUrls, errors };
};
